import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { scrollBehaviorForUser, scrollToSection } from '../utils/scrollToSection'

/** Query: `?scroll=testar-gratis` scrolls to `getElementById('testar-gratis')`. */
const SCROLL_QUERY = 'scroll'

/** One follow-up after load/fonts settle — no staggered timeouts (those caused choppy jumps). */
function attachDeepLinkScroll(id, cleanupRef) {
  const behavior = scrollBehaviorForUser()

  const retry = () => {
    if (cleanupRef.cancelled) return
    scrollToSection(id, { behavior })
  }

  Promise.all([
    document.readyState === 'complete'
      ? Promise.resolve()
      : new Promise((resolve) => window.addEventListener('load', resolve, { once: true })),
    document.fonts?.ready ?? Promise.resolve(),
  ]).then(retry)

  return () => {
    cleanupRef.cancelled = true
  }
}

export default function ScrollToTop() {
  const location = useLocation()
  const navigate = useNavigate()
  const pathnameRef = useRef(location.pathname)
  const isInitialPageView = useRef(true)

  // Meta Pixel: track PageView on client-side route changes (initial load is in index.html).
  useEffect(() => {
    if (isInitialPageView.current) {
      isInitialPageView.current = false
      return
    }
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView')
    }
  }, [location.pathname])

  // Same-page # links: prevent native scroll + let hash effect run one smooth scroll.
  useEffect(() => {
    const onClick = (e) => {
      const anchor = e.target.closest('a[href*="#"]')
      if (!anchor) return

      const href = anchor.getAttribute('href')
      if (!href || href === '#') return

      let url
      try {
        url = new URL(href, window.location.href)
      } catch {
        return
      }

      if (url.origin !== window.location.origin) return
      if (url.pathname !== location.pathname) return

      const id = url.hash.replace(/^#/, '')
      if (!id) return

      e.preventDefault()
      navigate({ pathname: url.pathname, search: url.search, hash: id })
    }

    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [navigate, location.pathname])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const fromQuery = params.get(SCROLL_QUERY)?.trim()
    const fromHash = (location.hash || '').replace(/^#/, '').trim()
    const targetId = fromHash || fromQuery

    if (targetId) {
      const pathnameChanged = pathnameRef.current !== location.pathname
      pathnameRef.current = location.pathname
      const behavior = scrollBehaviorForUser()

      scrollToSection(targetId, { behavior })

      if (pathnameChanged || document.readyState !== 'complete') {
        const cleanupRef = { cancelled: false }
        return attachDeepLinkScroll(targetId, cleanupRef)
      }
      return
    }

    pathnameRef.current = location.pathname
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [location.pathname, location.search, location.hash])

  return null
}
